/**
 * Weather host — one shared current-conditions reading for the whole OS.
 * Exposed as ctx.weather.
 *
 * WHY THIS IS A HOST SERVICE
 * `ctx.time` already exists for the same reason: a wallpaper, a widget and a
 * theme all need one answer to "what is it like outside", and three modules
 * deriving it separately is three fetches, three caches, three cities and three
 * chances to disagree. The moment a *second* module wanted weather — a
 * wallpaper that rains when it is raining — the fetch belonged here.
 *
 * WHAT IT IS NOT
 * Not a forecast API. `widget.weather` still owns the 7-day outlook, the units
 * setting and the tile — that is presentation, and it is per-placement. This
 * publishes the single **current** reading, in SI, that a renderer can act on:
 * `cloud`, `precip`, `fog`, `storm` are 0..1 and mean exactly what they say.
 *
 * THE LOCATION IS OS-WIDE
 * `setLocation` is how a module says "this is where the user is". `widget.weather`
 * calls it with whatever city its settings resolve to, so the desktop follows the
 * tile the user configured. Two tiles pointed at two cities means last-writer-
 * wins for the OS location; each tile still renders its own place.
 *
 * COST
 * Nothing is fetched until someone subscribes, and the timer stops when the last
 * listener leaves — a desktop with no weather-aware module on it makes no
 * requests. Refreshes are 10 minutes apart, matching the proxy's own TTL for
 * these hosts, so most calls are served out of the server's response cache.
 *
 * DEGRADATION
 * Every failure keeps the last good reading and marks it `stale`. `get()` never
 * throws and `refresh()` never rejects: a consumer of this API is usually a
 * render loop, and a render loop has nowhere to put an exception.
 *
 * RECOVERY
 * A failing poll speeds UP rather than gives up — see nextPollDelayMs. The failure
 * this is for is transient (a laptop waking to a resolver that died while it
 * slept), and a fixed ten-minute interval meant the tile sat there reading
 * "unavailable" for ten minutes after the network was already back, unless the
 * user happened to tap it.
 */

import type { WeatherSnapshot } from "@arclog/module-sdk";
import {
  FRESH_MS,
  HTTP_TIMEOUT_MS,
  REFRESH_MS,
  currentUrl,
  geocodeUrl,
  messageFor,
  nextPollDelayMs,
  normalizeLocation,
  parseCurrent,
  parseGeocode,
  reviveSnapshot,
  snapshotFrom,
} from "./weather-model.mjs";

/** The one thing this needs from the outside: an allowlisted GET. */
export type WeatherFetch = (
  url: string,
  opts?: { timeoutMs?: number },
) => Promise<{ ok: boolean; status: number; json<T = unknown>(): Promise<T> }>;

export type WeatherApi = {
  get(): WeatherSnapshot | null;
  subscribe(fn: (s: WeatherSnapshot | null) => void): () => void;
  refresh(opts?: { force?: boolean }): Promise<WeatherSnapshot | null>;
  getLocation(): string;
  setLocation(name: string): void;
  getError(): string | null;
  setOverride(patch: Partial<WeatherSnapshot> | null): void;
  getOverride(): Partial<WeatherSnapshot> | null;
};

/**
 * The reading an override starts from when there is no real one yet — a clear
 * day, so `setOverride({ precip: 1 })` on a machine that has never fetched
 * still previews rain instead of returning null.
 */
const BASE: WeatherSnapshot = {
  place: "Preview",
  code: 0,
  kind: "clear",
  label: "Clear",
  tempC: 18,
  windKph: 8,
  cloud: 0.15,
  precip: 0,
  fog: 0,
  storm: 0,
  isDay: null,
  at: 0,
  stale: false,
};

const LOCATION_KEY = "arclog.weather.location";
const SNAPSHOT_KEY = "arclog.weather.last";
/** Geocoding is expensive and a city does not move. Cached for a day. */
const GEO_KEY = "arclog.weather.geo";
const GEO_TTL_MS = 24 * 60 * 60_000;

type Geo = { lat: number; lon: number; place: string; at: number };

function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* quota or private mode — the in-memory copy still works */
  }
}

class WeatherBus {
  private listeners = new Set<(s: WeatherSnapshot | null) => void>();
  private snapshot: WeatherSnapshot | null = null;
  private error: string | null = null;
  private location: string;
  private geo: Record<string, Geo>;
  private timer: number | null = null;
  /** True between the first subscriber and the last unsubscribe — one loop, ever. */
  private active = false;
  /** A run already awaiting refresh(), so a visibility wake cannot double-count. */
  private running = false;
  /** Consecutive failed refreshes. Indexes BACKOFF_MS; any success zeroes it. */
  private failures = 0;
  /** The in-flight refresh, so N subscribers arriving at once make ONE request. */
  private pending: Promise<WeatherSnapshot | null> | null = null;
  /**
   * Deliberately in memory only, exactly like ctx.time's override: a preview
   * that survived a reload would leave the desktop permanently snowing with no
   * obvious way back.
   */
  private override: Partial<WeatherSnapshot> | null = null;

  constructor(private http: WeatherFetch) {
    this.location = normalizeLocation(localStorage.getItem?.(LOCATION_KEY));
    this.geo = readJson<Record<string, Geo>>(GEO_KEY) || {};
    // A cached reading paints the first frame instead of a blank sky, and is
    // immediately marked stale so nothing presents it as current.
    this.snapshot = reviveSnapshot(readJson(SNAPSHOT_KEY));
  }

  /** What consumers see: the real reading, with any preview patched over it. */
  get(): WeatherSnapshot | null {
    if (!this.override) return this.snapshot;
    return { ...(this.snapshot || BASE), ...this.override };
  }

  setOverride(patch: Partial<WeatherSnapshot> | null) {
    this.override = patch && typeof patch === "object" ? patch : null;
    this.emit();
  }

  getOverride(): Partial<WeatherSnapshot> | null {
    return this.override;
  }

  getError(): string | null {
    return this.error;
  }

  getLocation(): string {
    return this.location;
  }

  setLocation(name: string) {
    const next = normalizeLocation(name);
    if (next.toLowerCase() === this.location.toLowerCase()) return;
    this.location = next;
    writeJson(LOCATION_KEY, next);
    // The old city's reading is not a stale version of the new one, it is the
    // wrong city — showing it under a new name would be actively misleading.
    this.snapshot = null;
    this.error = null;
    this.emit();
    void this.refresh({ force: true });
  }

  subscribe(fn: (s: WeatherSnapshot | null) => void): () => void {
    this.listeners.add(fn);
    // Called immediately, like ctx.time.subscribe — a consumer should never
    // need a separate get() to paint its first frame.
    try {
      fn(this.get());
    } catch {
      /* a listener that throws must not break the others */
    }
    this.start();
    return () => {
      this.listeners.delete(fn);
      if (this.listeners.size === 0) this.stop();
    };
  }

  private start() {
    if (this.active) return;
    if (typeof window === "undefined") return;
    this.active = true;
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", this.onVisible);
    }
    void this.run();
  }

  private stop() {
    this.active = false;
    this.clearTimer();
    // The next subscriber starts from a clean slate: the old failure count
    // belongs to a desktop nobody is looking at any more.
    this.failures = 0;
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", this.onVisible);
    }
  }

  private clearTimer() {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
  }

  /** The ONLY place a timer is armed, and it always clears first — never two. */
  private schedule(ms: number) {
    if (!this.active) return;
    this.clearTimer();
    this.timer = window.setTimeout(() => void this.run(), ms);
  }

  /** One poll: refresh, then decide how long until the next one. */
  private async run(): Promise<void> {
    if (!this.active || this.running) return;
    // A hidden tab must not spin the backoff. Nothing is painted, and a
    // backgrounded desktop retrying every 15s is how a deployment gets itself
    // refused by a free public API. Sleep at the normal interval; onVisible
    // below polls the instant the tab comes back, which is when it matters.
    if (typeof document !== "undefined" && document.hidden) {
      this.schedule(REFRESH_MS);
      return;
    }
    this.running = true;
    try {
      await this.refresh();
    } finally {
      this.running = false;
    }
    // refresh() never rejects (see the class doc), so the outcome is read off
    // `error` — which it sets to null on success and a message on failure —
    // rather than out of a catch that could never fire.
    this.failures = this.error === null ? 0 : this.failures + 1;
    this.schedule(nextPollDelayMs(this.failures));
  }

  /**
   * Coming back to the tab is the one moment a stale reading is most obvious,
   * so it polls immediately instead of serving out whatever the backoff had
   * queued. The in-flight guard in run() means this cannot stack a second poll.
   */
  private onVisible = (): void => {
    if (!this.active || typeof document === "undefined" || document.hidden) return;
    this.clearTimer();
    void this.run();
  };

  private emit() {
    const s = this.get();
    for (const fn of this.listeners) {
      try {
        fn(s);
      } catch {
        /* as above */
      }
    }
  }

  refresh(opts?: { force?: boolean }): Promise<WeatherSnapshot | null> {
    const fresh =
      this.snapshot && !this.snapshot.stale && Date.now() - this.snapshot.at < FRESH_MS;
    if (fresh && !opts?.force) return Promise.resolve(this.get());
    if (this.pending) return this.pending;

    this.pending = this.fetchOnce()
      .then((s) => {
        this.snapshot = s;
        this.error = null;
        writeJson(SNAPSHOT_KEY, s);
        this.emit();
        // Never the raw reading: with a preview active, a caller awaiting
        // refresh() must see what is actually on screen.
        return this.get();
      })
      .catch((e: unknown) => {
        this.error = messageFor(e);
        // Keep the last reading rather than blanking: an offline desktop should
        // degrade to "the weather as of an hour ago", not to no weather at all.
        if (this.snapshot && !this.snapshot.stale) {
          this.snapshot = { ...this.snapshot, stale: true };
        }
        this.emit();
        return this.get();
      })
      .finally(() => {
        this.pending = null;
      });

    return this.pending;
  }

  private async fetchOnce(): Promise<WeatherSnapshot> {
    const location = this.location;
    const geo = await this.resolveGeo(location);
    const res = await this.http(currentUrl(geo.lat, geo.lon), { timeoutMs: HTTP_TIMEOUT_MS });
    const cur = parseCurrent(await res.json());
    return snapshotFrom(geo.place, cur, Date.now());
  }

  private async resolveGeo(location: string): Promise<Geo> {
    const key = location.toLowerCase();
    const hit = this.geo[key];
    if (hit && Date.now() - hit.at < GEO_TTL_MS) return hit;

    const res = await this.http(geocodeUrl(location), { timeoutMs: HTTP_TIMEOUT_MS });
    const parsed = parseGeocode(await res.json(), location);
    const rec: Geo = { ...parsed, at: Date.now() };
    this.geo[key] = rec;
    writeJson(GEO_KEY, this.geo);
    return rec;
  }
}

export function createWeatherApi(http: WeatherFetch): WeatherApi {
  const bus = new WeatherBus(http);
  return {
    get: () => bus.get(),
    subscribe: (fn) => bus.subscribe(fn),
    refresh: (opts) => bus.refresh(opts),
    getLocation: () => bus.getLocation(),
    setLocation: (name) => bus.setLocation(name),
    getError: () => bus.getError(),
    setOverride: (patch) => bus.setOverride(patch),
    getOverride: () => bus.getOverride(),
  };
}
